import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Chart from 'react-apexcharts';
import Loader from '../../component/common/Loader';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  PANEL_ROW_CLASS,
  Chip,
  EntityHeader,
  BackLink,
  STAT_ICONS,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID,
  CHART_GRID_NO_X,
  toneChip,
  paletteAt
} from '../../component/common/designSystem';
import { qualityChipClass, sleepTargetAnnotation } from './sleepTheme';
import { fetchSleepUserDetail, minsToHhMm } from './sleepHubApi';

const dayLabel = (date) =>
  new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

/**
 * Sleep → User detail.
 * One user's combined sleep profile: tracker stats + charts and their
 * sleep-sound listening history side by side.
 * Reused by the company panel via the `fetcher` / `backPath` / `backLabel`
 * props; without them it behaves as the super-admin page (back destination
 * derived from the B2B/B2C flow query params).
 */
const SleepUserDetail = ({
  fetcher = fetchSleepUserDetail,
  backPath: backPathProp,
  backLabel: backLabelProp
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');
  // Present only when arriving from a company page (B2B flow); it decides
  // where "Back" goes and which list a missing userId falls back to.
  const companyId = searchParams.get('companyId');

  const backPath =
    backPathProp || (companyId ? `/sleep/b2b/company/${companyId}` : '/sleep/b2c');
  const backLabel = backLabelProp || (companyId ? 'Back to Company' : 'Back to B2C Logs');

  const [loader, setLoader] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!userId) {
      navigate(backPath, { replace: true });
      return;
    }
    let mounted = true;
    setLoader(true);
    fetcher(userId).then((data) => {
      if (!mounted) return;
      setDetail(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fetcher]);

  const user = detail?.user;
  const sleepLogs = detail?.sleepLogs || [];
  const soundHistory = detail?.soundHistory || [];
  const topSounds = detail?.topSounds || [];

  const durationOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.brand],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: sleepLogs.map((log) => dayLabel(log.date)) },
    yaxis: { labels: { formatter: (val) => minsToHhMm(val) } },
    tooltip: { y: { formatter: (val) => minsToHhMm(val) } },
    annotations: sleepTargetAnnotation
  };

  const scoreOptions = {
    chart: { ...CHART_BASE, type: 'line' },
    colors: [CHART_COLORS.brandSoft],
    stroke: { curve: 'smooth', width: 3 },
    markers: { size: 4 },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: sleepLogs.map((log) => dayLabel(log.date)) },
    yaxis: { min: 0, max: 10 },
    tooltip: { y: { formatter: (val) => `${val} / 10` } }
  };

  const topSoundsOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: topSounds.map((entry, index) => paletteAt(index)),
    plotOptions: {
      bar: { horizontal: true, borderRadius: 4, barHeight: '60%', distributed: true }
    },
    dataLabels: { enabled: true, style: { fontSize: '12px' } },
    legend: { show: false },
    grid: CHART_GRID_NO_X,
    xaxis: { categories: topSounds.map((entry) => entry.sound) },
    tooltip: { y: { formatter: (val) => `${val} plays` } }
  };

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>User Sleep Detail | Shoorah Admin</title>
      </Helmet>
      {loader ? <Loader /> : null}

      <BackLink label={backLabel} onClick={() => navigate(backPath)} />

      {user && (
        <EntityHeader
          className="mt-4"
          image={user.user_profile}
          name={user.name}
          subtitle={user.email}
          action={
            <Chip className={toneChip('brand')}>
              {user.accountType === 'B2B' ? `B2B — ${user.company}` : 'B2C'}
            </Chip>
          }
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mt-6">
        <StatCard
          icon={STAT_ICONS.time}
          title="Avg Sleep Hours"
          value={user ? minsToHhMm(user.avgSleepMins) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.logs}
          title="Total Sleep Logs"
          value={user ? user.totalLogs : '-'}
        />
        <StatCard
          icon={STAT_ICONS.score}
          title="Avg Sleep Score"
          value={user ? `${user.avgScore} / 10` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.wearable}
          title="Wearable"
          value={user ? (user.wearableConnected ? 'Connected' : 'Not Connected') : '-'}
        />
        <StatCard
          icon={STAT_ICONS.audioLibrary}
          title="Most Heard Sound"
          value={detail?.mostHeardSound || '-'}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Sleep Duration" subtitle="Last 14 logged nights">
          {sleepLogs.length > 0 ? (
            <Chart
              type="bar"
              height={320}
              width="100%"
              series={[{ name: 'Duration', data: sleepLogs.map((log) => log.durationMins) }]}
              options={durationOptions}
            />
          ) : (
            <PanelEmpty>No sleep logs yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Sleep Score" subtitle="Last 14 logged nights">
          {sleepLogs.length > 0 ? (
            <Chart
              type="line"
              height={320}
              width="100%"
              series={[{ name: 'Score', data: sleepLogs.map((log) => log.score) }]}
              options={scoreOptions}
            />
          ) : (
            <PanelEmpty>No sleep logs yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Sleep Logs" subtitle="Most recent nights">
          {sleepLogs.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Date</th>
                  <th className="py-2 pr-4 font-medium text-right">Duration</th>
                  <th className="py-2 pr-4 font-medium text-right">Score</th>
                  <th className="py-2 pr-4 font-medium">Quality</th>
                  <th className="py-2 font-medium">Source</th>
                </>
              }
            >
              {[...sleepLogs].reverse().map((log) => (
                <tr key={log.date} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{log.date}</td>
                  <td className="py-2 pr-4 text-right">{minsToHhMm(log.durationMins)}</td>
                  <td className="py-2 pr-4 text-right">{log.score} / 10</td>
                  <td className="py-2 pr-4">
                    <Chip className={qualityChipClass(log.quality)}>{log.quality}</Chip>
                  </td>
                  <td className="py-2">{log.source}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No sleep logs yet.</PanelEmpty>
          )}
        </Panel>

        <div className="flex flex-col gap-6">
          <Panel title="Top Sleep Sounds" subtitle="This user's most played sounds">
            {topSounds.length > 0 ? (
              <Chart
                type="bar"
                height={220}
                width="100%"
                series={[{ name: 'Plays', data: topSounds.map((entry) => entry.plays) }]}
                options={topSoundsOptions}
              />
            ) : (
              <PanelEmpty>No sound activity yet.</PanelEmpty>
            )}
          </Panel>

          <Panel title="Sound Listening History" subtitle="Most recent sessions">
            {soundHistory.length > 0 ? (
              <PanelTable
                head={
                  <>
                    <th className="py-2 pr-4 font-medium">Date</th>
                    <th className="py-2 pr-4 font-medium">Sound</th>
                    <th className="py-2 pr-4 font-medium text-right">Plays</th>
                    <th className="py-2 font-medium text-right">Listened</th>
                  </>
                }
              >
                {soundHistory.map((session, index) => (
                  <tr key={`${session.date}-${index}`} className={PANEL_ROW_CLASS}>
                    <td className="py-2 pr-4">{session.date}</td>
                    <td className="py-2 pr-4">{session.sound}</td>
                    <td className="py-2 pr-4 text-right">{session.plays}</td>
                    <td className="py-2 text-right">{minsToHhMm(session.listenMins)}</td>
                  </tr>
                ))}
              </PanelTable>
            ) : (
              <PanelEmpty>No listening history yet.</PanelEmpty>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
};

export default SleepUserDetail;
